import { Button } from "@/components/ui/button";
import type { SocialPlatform } from "@content-os/config";
import { PlatformIcon } from "@/components/social/PlatformIcon";
import type { SocialAccount } from "@/hooks/use-accounts";
import { useTranslation } from "react-i18next";

const PLATFORMS = [
  "INSTAGRAM",
  "TIKTOK",
  "FACEBOOK",
  "YOUTUBE",
  "LINKEDIN",
  "TWITTER",
  "THREADS",
  "PINTEREST",
  "REDDIT",
  "GOOGLEBUSINESS",
] as const;

const PLATFORM_LABEL: Record<string, string> = {
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  FACEBOOK: "Facebook",
  YOUTUBE: "YouTube",
  LINKEDIN: "LinkedIn",
  TWITTER: "X",
  THREADS: "Threads",
  PINTEREST: "Pinterest",
  REDDIT: "Reddit",
  GOOGLEBUSINESS: "Google Business",
};

interface Props {
  accounts: SocialAccount[];
  onConnect: (platform: string) => void;
  busyPlatform?: string | null;
  disabled?: boolean;
}

export function PlatformConnectGrid({ accounts, onConnect, busyPlatform, disabled }: Props) {
  const { t } = useTranslation("settings");
  const connected = new Set(accounts.filter((a) => a.status === "ACTIVE").map((a) => a.platform));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {PLATFORMS.map((p) => {
        const isConnected = connected.has(p);
        const username = accounts.find((a) => a.platform === p && a.status === "ACTIVE")?.username;
        return (
          <div key={p} className="rounded-lg border p-3 flex flex-col items-center gap-2">
            <PlatformIcon platform={p as SocialPlatform} size={40} className="rounded" />
            <div className="text-sm font-medium">{PLATFORM_LABEL[p] ?? p}</div>
            {isConnected ? (
              <div className="text-xs text-emerald-700 text-center">{username ? t("platformGrid.connected", { username }) : t("platformGrid.connectedDefault")}</div>
            ) : (
              <Button
                size="sm"
                onClick={() => onConnect(p)}
                disabled={disabled || busyPlatform === p}
              >
                {busyPlatform === p ? t("platformGrid.opening") : t("platformGrid.connect")}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
